import React, { useState } from 'react';

const About = () => {
  const [activeTab, setActiveTab] = useState(0);

  const missionItems = [
    { icon: '🔍', text: 'To introduce verified and accountable workforce management systems across railway goods sheds.' },
    { icon: '💻', text: 'To promote digital transparency through monitoring, reporting, and operational oversight.' },
    { icon: '⚡', text: 'To enhance productivity and operational efficiency in freight handling activities.' },
    { icon: '🛡️', text: 'To strengthen worker welfare through safety measures, healthcare support, training, and social security initiatives.' },
    { icon: '🚂', text: 'To contribute towards the modernization and sustainable growth of India\'s railway freight ecosystem.' },
  ];

  const values = [
    { icon: '⚖️', title: 'Integrity', desc: 'Unwavering commitment to transparency and ethical governance at every level of operation.' },
    { icon: '🚀', title: 'Innovation', desc: 'Leveraging technology and digital systems to drive operational excellence and efficiency.' },
    { icon: '🤝', title: 'Inclusion', desc: 'Equal opportunities, worker welfare and social security for every member of the SGCL family.' },
    { icon: '🌱', title: 'Sustainability', desc: 'Building long-term value for the nation, the workforce, and India\'s railway freight ecosystem.' },
  ];

  const stats = [
    { value: '500+', label: 'Workers Enrolled' },
    { value: '3+', label: 'Goods Sheds Covered' },
    { value: '2026', label: 'Year of Incorporation' },
    { value: '24/7', label: 'Support Available' },
  ];

  const tabs = [
    {
      id: 0, label: 'Overview', icon: '🏢',
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-slate-600 text-base leading-8 font-semibold mb-5 text-justify">
              Shramjivi Goodshed Corporation Limited (SGCL) is a company incorporated under the Ministry of Corporate Affairs (MCA), Government of India, with the objective of transforming railway goods shed operations through structured workforce management, digital monitoring systems, and welfare-oriented infrastructure.
            </p>
            <p className="text-slate-500 text-[15px] leading-8 font-medium text-justify">
              The Company seeks to establish a transparent, accountable, and technology-driven operational framework that enhances efficiency, protects railway revenue, and improves the working conditions of goods shed workers across India.
            </p>
            <div className="mt-8 bg-slate-900 text-white p-6 rounded-2xl border-b-4 border-govt-orange flex items-center gap-5">
              <span className="text-4xl">🏆</span>
              <div>
                <p className="text-govt-orange text-[10px] font-black uppercase tracking-widest mb-1">Our Motto</p>
                <p className="text-lg font-black italic font-hindi">"श्रमेव जयते — Structured Labour, Stronger Railways"</p>
              </div>
            </div>
          </div>
          {/* Image collage */}
          <div className="grid grid-cols-2 gap-3 h-[380px]">
            <div className="relative row-span-2 rounded-2xl overflow-hidden shadow-xl">
              <img src="/SG (6).jpg" alt="Goods shed operations" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-govt-blue/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-[10px] font-black uppercase tracking-widest bg-govt-orange/90 px-2 py-1 rounded-md">Operations</span>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img src="/SG (7).jpg" alt="Railway freight" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-govt-blue/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-[10px] font-black uppercase tracking-widest bg-govt-blue/90 px-2 py-1 rounded-md">Freight</span>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img src="/SG (8).jpg" alt="Workforce" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-govt-blue/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-[10px] font-black uppercase tracking-widest bg-[#138808]/90 px-2 py-1 rounded-md">Workforce</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 1, label: 'Mission', icon: '🎯',
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="space-y-4">
            {missionItems.map((item, idx) => (
              <div key={idx} className="flex gap-5 items-start bg-slate-50 p-5 rounded-2xl border border-slate-100 hover:border-govt-blue hover:shadow-md transition-all duration-300 group">
                <div className="w-12 h-12 bg-govt-blue group-hover:bg-govt-orange rounded-xl flex items-center justify-center text-xl shrink-0 transition-colors duration-300 shadow-md">
                  {item.icon}
                </div>
                <div className="flex-1">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Point 0{idx + 1}</span>
                  <p className="text-slate-600 text-sm font-semibold leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[450px] sticky top-0">
            <img src="/SG (9).jpg" alt="Railway mission" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#001529] via-[#001529]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="text-govt-orange text-[10px] font-black uppercase tracking-widest mb-2">Vision Statement</p>
              <p className="text-white text-xl font-black italic leading-snug">
                "To transform Indian Railways into the most efficient, transparent, and worker-friendly freight logistics network."
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 2, label: 'Values', icon: '💎',
      content: (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
            {values.map((v, i) => (
              <div key={i} className="group bg-white rounded-3xl shadow-lg border border-slate-100 p-8 hover:-translate-y-2 hover:shadow-2xl hover:border-govt-blue transition-all duration-400 flex gap-5">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-3xl shrink-0 group-hover:bg-govt-blue group-hover:scale-110 transition-all duration-300 shadow-sm">
                  {v.icon}
                </div>
                <div>
                  <h3 className="font-black text-govt-blue text-base uppercase mb-2">{v.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed font-semibold">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Image banner */}
          <div className="relative rounded-3xl overflow-hidden h-52 shadow-xl">
            <img src="/g1.png" alt="SGCL values in action" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-govt-blue via-govt-blue/60 to-transparent" />
            <div className="absolute inset-0 flex items-center px-10">
              <div>
                <p className="text-govt-orange text-[10px] font-black uppercase tracking-widest mb-2">Building Together</p>
                <p className="text-white text-2xl md:text-3xl font-black italic uppercase leading-tight max-w-lg">
                  Every worker. Every shed.<br />Every day.
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-slate-50 min-h-screen font-sans pb-20">

      {/* ── 1. HERO with background image ── */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '420px' }}>
        {/* Background image */}
        <img
          src="/g3.png"
          alt="SGCL Operations"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d2d6b] via-[#0d2d6b]/85 to-[#0d2d6b]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001529]/80 via-transparent to-transparent" />

        {/* Decorative pattern */}
        <div className="absolute top-0 right-0 opacity-5 text-[220px] font-black select-none leading-none -mr-10 -mt-10">SGCL</div>

        <div className="relative z-10 px-4 md:px-20 py-20 max-w-5xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">
            About the Corporation
          </span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-6 leading-tight uppercase italic">
            Who We <span className="text-govt-orange">Are</span>
          </h1>
          <p className="text-lg md:text-xl text-blue-100 font-bold border-l-4 border-govt-orange pl-6 italic max-w-2xl leading-relaxed mb-8">
            "To transform Indian Railways into the most efficient, transparent, and worker-friendly freight logistics network in India."
          </p>

          {/* CIN badge */}
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-3">
            <span className="text-govt-orange text-lg">🏛️</span>
            <div>
              <p className="text-white/60 text-[9px] font-black uppercase tracking-widest">CIN Number</p>
              <p className="text-white font-black font-mono text-sm tracking-wide">U78300WR2026PLC293826</p>
            </div>
          </div>
        </div>

        {/* Tricolor bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      {/* ── 2. STATS STRIP ── */}
      <div className="bg-govt-blue text-white shadow-xl">
        <div className="container mx-auto px-4 md:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={i} className="py-6 px-4 text-center group hover:bg-white/5 transition-colors">
                <p className="text-2xl md:text-3xl font-black text-govt-orange">{s.value}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-blue-200 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3. TABBED CONTENT SECTION ── */}
      <div className="container mx-auto px-4 md:px-20 mt-14">
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden">

          {/* Tab Headers */}
          <div className="flex border-b border-slate-100 overflow-x-auto no-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-8 py-5 font-black text-xs uppercase tracking-widest whitespace-nowrap border-b-4 transition-all duration-200
                  ${activeTab === tab.id
                    ? 'border-govt-orange text-govt-blue bg-orange-50/50'
                    : 'border-transparent text-slate-400 hover:text-govt-blue hover:bg-slate-50'
                  }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-8 md:p-12">
            {tabs[activeTab].content}
          </div>
        </div>
      </div>

      {/* ── 4. FULL-WIDTH IMAGE FEATURE STRIP ── */}
      <div className="mt-16 relative overflow-hidden" style={{ height: '320px' }}>
        <img src="/g1.png" alt="Goods shed workforce" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529]/95 via-[#001529]/70 to-transparent" />
        <div className="absolute inset-0 container mx-auto px-4 md:px-20 flex items-center">
          <div className="max-w-xl">
            <p className="text-govt-orange text-[10px] font-black uppercase tracking-widest mb-3">Our Identity</p>
            <h2 className="text-white text-3xl md:text-4xl font-black italic uppercase leading-tight mb-4">
              Empowering Every<br />Goodshed Worker
            </h2>
            <p className="text-blue-200 text-sm font-semibold leading-relaxed">
              SGCL bridges the gap between railway administration and the workforce — ensuring safety, welfare, and digital accountability across India's goods shed network.
            </p>
          </div>
        </div>
        {/* Tricolor accent */}
        <div className="absolute bottom-0 left-0 right-0 h-1 flex">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-white opacity-60" />
          <div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      {/* ── 5. CORPORATE IDENTITY PANEL ── */}
      <div className="container mx-auto px-4 md:px-20 mt-14">
        <div className="bg-gradient-to-br from-[#001c3d] via-[#002e5b] to-[#001020] rounded-[2.5rem] shadow-2xl border border-white/10 overflow-hidden relative">

          {/* Saffron, White, Green Tricolor Header Strip */}
          <div className="h-1.5 flex">
            <div className="w-1/3 bg-[#FF9933] h-full" />
            <div className="w-1/3 bg-white h-full" />
            <div className="w-1/3 bg-[#138808] h-full" />
          </div>

          {/* Main Container */}
          <div className="relative p-8 md:p-12 z-10">

            {/* Glowing Golden Ashoka Chakra Watermark */}
            <div className="absolute right-12 bottom-12 pointer-events-none opacity-[0.04] select-none hidden lg:block">
              <svg className="w-80 h-80 text-[#FF9933]" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="50" cy="50" r="7" fill="currentColor" />
                {Array.from({ length: 24 }).map((_, i) => {
                  const angle = (i * 360) / 24;
                  return (
                    <line
                      key={i}
                      x1="50"
                      y1="50"
                      x2={50 + 38 * Math.cos((angle * Math.PI) / 180)}
                      y2={50 + 38 * Math.sin((angle * Math.PI) / 180)}
                      stroke="currentColor"
                      strokeWidth="1.2"
                    />
                  );
                })}
              </svg>
            </div>

            {/* Header Section */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pb-8 border-b border-white/10 mb-10">
              <div className="flex items-center gap-5">
                {/* Glowing Golden/Blue Chakra Seal */}
                <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center border border-white/15 shadow-inner shrink-0">
                  <svg className="w-10 h-10 text-[#FF9933]" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="2.5" />
                    <circle cx="50" cy="50" r="8" fill="currentColor" />
                    {Array.from({ length: 24 }).map((_, i) => {
                      const angle = (i * 360) / 24;
                      return (
                        <line
                          key={i}
                          x1="50"
                          y1="50"
                          x2={50 + 37 * Math.cos((angle * Math.PI) / 180)}
                          y2={50 + 37 * Math.sin((angle * Math.PI) / 180)}
                          stroke="currentColor"
                          strokeWidth="1.2"
                        />
                      );
                    })}
                  </svg>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-govt-orange text-[10px] font-black uppercase tracking-widest leading-none mb-1.5">Ministry of Corporate Affairs Verified</p>
                  <h3 className="font-black text-white text-xl md:text-2xl uppercase tracking-wide">Corporate Identity &amp; Profile</h3>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-5 py-2 shadow-sm shrink-0">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-green-400 text-[10px] font-black uppercase tracking-widest">MCA Active Entity</span>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

              {/* Left Side: Corporate Details Card */}
              <div className="lg:col-span-8 space-y-6">

                {/* Company Name Box */}
                <div className="bg-white/[0.03] backdrop-blur-md p-6 rounded-2xl border-l-4 border-[#FF9933] border-t border-r border-b border-white/5 shadow-lg">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Company Name</span>
                  <h4 className="text-lg md:text-xl font-black text-white uppercase tracking-tight leading-snug">
                    Shramjivi Goodshed Corporation Limited
                  </h4>
                  <p className="text-slate-400 text-[11px] font-bold mt-1 uppercase tracking-wider">
                    Registered under the Companies Act, 2013 (Govt. of India)
                  </p>
                </div>

                {/* Grid of Identifiers */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: 'CIN Number', value: 'U78300WR2026PLC293826', badge: 'CIN' },
                    { label: 'PAN Number', value: 'ABTCS6177K', badge: 'PAN' },
                    { label: 'GSTIN Registration', value: '19ABTCS6177K1Z9', badge: 'GSTIN' }
                  ].map((item, i) => (
                    <div key={i} className="bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300 p-5 rounded-2xl border border-white/5 hover:border-white/10 shadow-md">
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block mb-2">{item.label}</span>
                      <div className="flex flex-col gap-1.5">
                        <span className="bg-[#FF9933]/15 text-[#FF9933] text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-[#FF9933]/20 w-max">
                          {item.badge}
                        </span>
                        <span className="font-black font-mono text-xs text-white select-all mt-1">
                          {item.value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Legal Status Bar */}
                <div className="bg-white/[0.03] p-5 rounded-2xl border-l-4 border-[#138808] border-t border-r border-b border-white/5 shadow-md flex items-center gap-4">
                  <div className="text-2xl">🛡️</div>
                  <div>
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-0.5">Legal Status</span>
                    <p className="font-black text-xs sm:text-sm text-slate-200 uppercase tracking-wide">
                      Active Public Limited Corporation registered under MCA, Government of India
                    </p>
                  </div>
                </div>

              </div>

              {/* Right Side: Registered Office & Motto */}
              <div className="lg:col-span-4 flex flex-col justify-between gap-8">

                {/* Registered Office Box */}
                <div className="bg-white/[0.03] backdrop-blur-md p-6 rounded-2xl border border-white/5 shadow-md relative overflow-hidden flex-1">
                  <div className="absolute right-0 top-0 opacity-[0.02] text-8xl pointer-events-none text-white">📍</div>
                  <div className="flex items-center gap-2.5 mb-4 border-b border-white/10 pb-3">
                    <span className="text-govt-orange text-lg">📍</span>
                    <h5 className="text-[10px] text-slate-400 font-black uppercase tracking-wider">Registered Office</h5>
                  </div>
                  <p className="text-white text-sm font-black uppercase tracking-wide leading-relaxed">
                    Siliguri, West Bengal — 734003
                  </p>
                  <p className="text-slate-400 text-[11px] font-semibold mt-2 uppercase tracking-wide leading-relaxed">
                    D/14, Satyajit Roy Co-operative Market, Champasari More, Siliguri, West Bengal
                  </p>
                </div>

                {/* Corporate Motto Box */}
                <div className="bg-white/[0.03] p-6 rounded-2xl border border-white/5 shadow-md relative overflow-hidden flex-1 flex flex-col justify-center border-l-4 border-govt-orange">
                  {/* Flag Background Accent */}
                  <div className="absolute top-0 right-0 w-24 h-full opacity-5 pointer-events-none flex">
                    <div className="w-1/3 bg-[#FF9933] h-full" />
                    <div className="w-1/3 bg-white h-full" />
                    <div className="w-1/3 bg-[#138808] h-full" />
                  </div>

                  <div className="flex items-center gap-2 mb-3 border-b border-white/10 pb-3">
                    <span className="text-govt-orange text-lg">🎯</span>
                    <h5 className="text-[10px] text-slate-400 font-black uppercase tracking-wider">Corporate Motto</h5>
                  </div>
                  <p className="text-govt-orange text-xl font-black italic tracking-wide font-hindi leading-none mb-1">
                    "श्रमेव जयते"
                  </p>
                  <p className="text-white text-xs font-black leading-relaxed uppercase tracking-widest mt-2">
                    Structured Labour,<br />
                    <span className="text-govt-orange">Stronger Railways</span>
                  </p>
                </div>

              </div>

            </div>

          </div>
        </div>
      </div>

      {/* ── 6. OFFICE ADDRESSES ── */}
      <div className="container mx-auto px-4 md:px-20 mt-14">
        <div className="text-center mb-10">
          <span className="bg-orange-100 text-govt-orange px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Our Offices</span>
          <h2 className="text-3xl font-black text-govt-blue mt-4 uppercase italic">Where We Operate</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              emoji: '🏢', label: 'Registered Office', tag: 'Siliguri, WB Office',
              address: ['D/14, Satyajit Roy Co-operative Market,', 'Champasari More, Ward No. 46,', 'Pradhan Nagar, Siliguri,', 'Darjeeling, West Bengal – 734003'],
              color: '#0d2d6b',
            },
            {
              emoji: '📍', label: 'Zonal Office', tag: 'Guwahati Office',
              address: ['15, Bhaskar Nagar School Path,', 'R.G. Baruah Road, Bamunimaidan,', 'Kamrup (Metro), Assam – 781021'],
              color: '#FF9933',
            },
            {
              emoji: '🏛️', label: 'Head Office', tag: 'New Delhi Office',
              address: ['D7, 1st Floor, Ranjit Nagar Commercial Complex, Behind Satyam Cinema,', 'Near Shadipur Metro Station, Gate No. 5,', 'New Delhi – 110008'],
              color: '#b45309',
            },
          ].map((office, i) => (
            <div key={i} className="group bg-white rounded-3xl shadow-xl border border-slate-100 hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col justify-between">
              {/* Color top bar */}
              <div>
                <div className="h-2" style={{ background: office.color }} />
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-md group-hover:scale-110 transition-transform duration-300" style={{ background: `${office.color}15` }}>
                      {office.emoji}
                    </div>
                    <div>
                      <h3 className="text-lg font-black uppercase tracking-wider" style={{ color: office.color }}>{office.label}</h3>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{office.tag}</span>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                    {office.address.map((line, j) => (
                      <p key={j} className="text-slate-600 text-sm font-semibold uppercase italic leading-7">{line}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 7. CONTACT CHANNELS ── */}
      <div className="container mx-auto px-4 md:px-20 mt-14">
        <div className="bg-govt-blue rounded-3xl shadow-2xl border-b-8 border-govt-orange p-8 md:p-12">
          <h3 className="text-2xl font-black text-white uppercase italic mb-8 text-center">
            Get In Touch
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { emoji: '🌐', label: 'NFR Website', value: 'www.shramjivicorp.com', href: 'https://www.shramjivicorp.com', isLink: true },
              { emoji: '🏢', label: 'Corporate Website', value: 'www.shramjivicorporation.co.in', href: 'https://www.shramjivicorporation.co.in', isLink: true },
            ].map((ch, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-colors text-center flex flex-col items-center group">
                <div className="w-14 h-14 bg-white/10 group-hover:bg-govt-orange rounded-2xl flex items-center justify-center text-2xl mb-4 transition-colors duration-300 border border-white/20">
                  {ch.emoji}
                </div>
                <p className="text-white/50 text-[10px] uppercase tracking-widest font-black mb-2">{ch.label}</p>
                {ch.isLink ? (
                  <a href={ch.href} target="_blank" rel="noopener noreferrer" className="text-white text-sm font-black hover:text-govt-orange transition-colors">
                    {ch.value}
                  </a>
                ) : (
                  <p className="text-white text-sm font-black">{ch.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default About;